import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Upload,
    FileText,
    TrendingUp,
    Activity,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    ArrowRight,
    ArrowLeft,
    Calendar,
    Clock,
    ChevronRight,
    ChevronLeft
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
    const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;
    const dateLocale = isRTL ? ar : enUS;
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, reportsRes] = await Promise.all([
                    axios.get(`${API}/stats`),
                    axios.get(`${API}/reports`)
                ]);
                setStats(statsRes.data);
                setReports(reportsRes.data);
            } catch (error) {
                console.error('Failed to fetch dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (user) fetchData();
    }, [user]);

    const getStatusIcon = (status) => {
        switch (status) {
            case 'normal':
                return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
            case 'abnormal':
                return <AlertTriangle className="w-4 h-4 text-amber-600" />;
            case 'critical':
                return <XCircle className="w-4 h-4 text-red-600" />;
            default:
                return null;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'normal':
                return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'abnormal':
                return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'critical':
                return 'bg-red-50 text-red-700 border-red-200';
            default:
                return 'bg-stone-50 text-stone-700 border-stone-200';
        }
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-32 rounded-2xl" />
                    ))}
                </div>
                <Skeleton className="h-64 rounded-2xl mb-6" />
                <Skeleton className="h-96 rounded-2xl" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
            {/* Welcome Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-stone-800">
                        {isRTL ? `مرحباً، ${user?.name?.split(' ')[0]}! 👋` : `Welcome back, ${user?.name?.split(' ')[0]}! 👋`}
                    </h1>
                    <p className="text-stone-500 mt-1">{t('dashboard.subtitle') || (isRTL ? 'نظرة عامة على بياناتك الصحية' : "Here's an overview of your health data")}</p>
                </div>
                <Link to="/upload">
                    <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-6 shadow-lg shadow-teal-500/25" data-testid="upload-new-btn">
                        <Upload className="w-4 h-4 ltr:mr-2 rtl:ml-2" />
                        {t('dashboard.uploadNew')}
                    </Button>
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="border-0 shadow-lg shadow-stone-200/50 hover:shadow-xl transition-shadow" data-testid="stat-total-reports">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-stone-500 mb-1">{t('dashboard.totalReports')}</p>
                                <p className="text-3xl font-bold text-stone-800 ltr-number">{stats?.total_reports || 0}</p>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center">
                                <FileText className="w-7 h-7 text-teal-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg shadow-stone-200/50 hover:shadow-xl transition-shadow" data-testid="stat-normal-values">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-stone-500 mb-1">{isRTL ? 'قيم طبيعية' : 'Normal Values'}</p>
                                <p className="text-3xl font-bold text-emerald-600 ltr-number">{stats?.status_counts?.normal || 0}</p>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center">
                                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg shadow-stone-200/50 hover:shadow-xl transition-shadow" data-testid="stat-attention-needed">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-stone-500 mb-1">{t('dashboard.needsAttention')}</p>
                                <p className="text-3xl font-bold text-amber-600 ltr-number">
                                    {(stats?.status_counts?.abnormal || 0) + (stats?.status_counts?.critical || 0)}
                                </p>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center">
                                <AlertTriangle className="w-7 h-7 text-amber-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Quick Actions */}
            {reports.length === 0 ? (
                <Card className="border-0 shadow-lg shadow-stone-200/50 mb-8" data-testid="empty-state">
                    <CardContent className="p-12 text-center">
                        <div className="w-20 h-20 rounded-3xl bg-teal-50 flex items-center justify-center mx-auto mb-6">
                            <Upload className="w-10 h-10 text-teal-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-stone-800 mb-2">{t('dashboard.uploadFirst')}</h3>
                        <p className="text-stone-500 mb-6 max-w-md mx-auto">
                            {isRTL ? 'ابدأ برفع تقرير تحليل. سنقوم بتحليله وتقديم شروحات سهلة الفهم.' : "Get started by uploading a lab report. We'll analyze it and provide easy-to-understand explanations."}
                        </p>
                        <Link to="/upload">
                            <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-teal-500/25">
                                {t('nav.upload')}
                                <ArrowIcon className="ltr:ml-2 rtl:mr-2 w-5 h-5" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Latest Report Summary */}
                    {stats?.latest_report && (
                        <Card className="border-0 shadow-lg shadow-stone-200/50 mb-8" data-testid="latest-report-card">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg font-semibold text-stone-800">{isRTL ? 'آخر تقرير' : 'Latest Report'}</CardTitle>
                                    <div className="flex items-center gap-2 text-sm text-stone-500">
                                        <Calendar className="w-4 h-4" />
                                        <span className="ltr-number">{format(new Date(stats.latest_report.upload_date), 'MMM d, yyyy', { locale: dateLocale })}</span>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col lg:flex-row gap-6">
                                    {/* AI Summary */}
                                    <div className="flex-1 p-4 bg-teal-50/50 rounded-2xl border border-teal-100">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Activity className="w-5 h-5 text-teal-600" />
                                            <span className="font-medium text-teal-700">{t('report.aiSummary')}</span>
                                        </div>
                                        <p className="text-stone-700 leading-relaxed">{stats.latest_report.ai_summary}</p>
                                    </div>

                                    {/* Quick Stats */}
                                    <div className="lg:w-64 space-y-3">
                                        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-emerald-700">{t('report.normal')}</span>
                                                <span className="font-semibold text-emerald-700 ltr-number">
                                                    {stats.latest_report.lab_values?.filter(v => v.status === 'normal').length || 0}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-amber-700">{t('report.abnormal')}</span>
                                                <span className="font-semibold text-amber-700 ltr-number">
                                                    {stats.latest_report.lab_values?.filter(v => v.status === 'abnormal').length || 0}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-red-700">{t('report.critical')}</span>
                                                <span className="font-semibold text-red-700 ltr-number">
                                                    {stats.latest_report.lab_values?.filter(v => v.status === 'critical').length || 0}
                                                </span>
                                            </div>
                                        </div>
                                        <Link to={`/report/${stats.latest_report.id}`}>
                                            <Button variant="outline" className="w-full mt-2 rounded-xl" data-testid="view-latest-report-btn">
                                                {isRTL ? 'عرض التقرير الكامل' : 'View Full Report'}
                                                <ChevronIcon className="w-4 h-4 ltr:ml-1 rtl:mr-1" />
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Recent Reports */}
                    <Card className="border-0 shadow-lg shadow-stone-200/50" data-testid="recent-reports-card">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-semibold text-stone-800">{isRTL ? 'جميع التقارير' : 'All Reports'}</CardTitle>
                                <Link to="/trends">
                                    <Button variant="ghost" size="sm" className="text-teal-600 hover:text-teal-700">
                                        <TrendingUp className="w-4 h-4 ltr:mr-1 rtl:ml-1" />
                                        {t('nav.trends')}
                                    </Button>
                                </Link>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {reports.map((report) => (
                                    <Link
                                        key={report.id}
                                        to={`/report/${report.id}`}
                                        className="flex items-center justify-between p-4 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors group"
                                        data-testid={`report-item-${report.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                                                <FileText className="w-6 h-6 text-teal-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-stone-800">{report.panel_type || (isRTL ? 'تقرير مختبر' : 'Lab Report')}</p>
                                                <div className="flex items-center gap-3 text-sm text-stone-500">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        <span className="ltr-number">{format(new Date(report.upload_date), 'MMM d, yyyy', { locale: dateLocale })}</span>
                                                    </span>
                                                    <span>{report.lab_values?.length || 0} tests</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="hidden sm:flex items-center gap-2">
                                                {report.lab_values?.slice(0, 3).map((value, i) => (
                                                    <Badge
                                                        key={i}
                                                        variant="outline"
                                                        className={`${getStatusColor(value.status)} border`}
                                                    >
                                                        {getStatusIcon(value.status)}
                                                    </Badge>
                                                ))}
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-teal-600 transition-colors" />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Medical Disclaimer */}
            <div className="mt-8 p-4 bg-stone-100 rounded-2xl">
                <p className="text-sm text-stone-600 text-center">
                    ⚠️ <strong>Medical Disclaimer:</strong> This tool provides educational information only and is not a medical diagnosis. 
                    Always consult with qualified healthcare professionals for medical advice.
                </p>
            </div>
        </div>
    );
};

export default DashboardPage;
